/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable require-jsdoc */

import { readMapMesg, saveMapMesg } from '@hfzq/crab-utils';
import { Mutex } from './mutex';

export interface CachedData<TData = any> {
  /** 储存的数据 */
  data: TData;
  /** 存储的时间点 */
  time: number;
  /** 存储多久 */
  cacheTime: number;
  /** 数据唯一标识 */
  id: string;
}

type CachedKey = string;
type idGenerator = () => Promise<string> | string;

export interface Storage<T = any> {
  set(key: CachedKey, data: T): Promise<this>;
  get(key: CachedKey): Promise<T | undefined>;
  clear(key?: string | string[]): Promise<void>;
}

/**
 * 控制数据缓存
 * 允许将数据缓存进指定的容器， 原生App，localStorage，内存
 * 允许设置数据的有效期
 *
 * idGenerator 为数据设置设置唯一id
 * 如果缓存中的id 和 当前生成的id不一致 则将当前数据视为无效数据
 */
export class LocalCacheStorage<T> {
  private idGenerator: idGenerator = () => 'default';

  private storage: Storage<CachedData>;

  constructor(storage: Storage, idGenerator?: () => Promise<string> | string) {
    this.storage = storage;
    if (idGenerator) {
      this.idGenerator = idGenerator;
    }
  }

  async setCache(key: CachedKey, cachedData: T, cacheTime = -1) {
    await this.storage.set(key, {
      data: cachedData,
      time: Date.now(),
      cacheTime,
      id: await this.idGenerator(),
    });
  }

  async getCache<TItem>(key: CachedKey): Promise<TItem | void> {
    const newId = await this.idGenerator();
    const cachedData = await this.storage.get(key);
    if (!cachedData) {
      return;
    }

    if (cachedData.id !== newId) {
      return;
    }

    if (cachedData.cacheTime > 0) {
      const currentTime = Date.now();
      const expiresTime = cachedData.time + cachedData.cacheTime;
      if (currentTime > expiresTime) {
        // await this.storage.clear(key);
        return;
      }
    }

    return cachedData.data;
  }

  async clear(key: string | string[]) {
    await this.storage.clear(key);
  }
}

/**
 * 将数据缓存进内存
 */
export class MemoryCache<T> implements Storage<T> {
  data = new Map<CachedKey, T>();

  async set(key: CachedKey, data: T) {
    this.data.set(key, data);
    return this;
  }

  async get(key: CachedKey) {
    return this.data.get(key);
  }

  async clear(key?: string | string[]) {
    if (key) {
      const cacheKeys = Array.isArray(key) ? key : [key];
      cacheKeys.forEach((cacheKey) => this.data.delete(cacheKey));
    } else {
      this.data.clear();
    }
  }
}

/**
 * 将数据缓存进App
 */
export class AppCache<T> implements Storage<T> {
  private mutex = new Mutex();

  CACHE_KEY: string;

  constructor(cacheKey: string) {
    this.CACHE_KEY = cacheKey;
  }

  async getCachedData(): Promise<Record<string, unknown>> {
    const res: any = await readMapMesg([this.CACHE_KEY]);
    const cacheData = res[this.CACHE_KEY];
    return cacheData ? JSON.parse(cacheData) : {};
  }

  async set(key: CachedKey, data: T) {
    await this.mutex.acquire();
    const cacheDataObj = await this.getCachedData();
    cacheDataObj[key] = data;
    try {
      await saveMapMesg({
        [this.CACHE_KEY]: JSON.stringify(cacheDataObj),
      });
    } finally {
      this.mutex.release();
    }
    return this;
  }

  async get(key: CachedKey): Promise<T | undefined> {
    const res = await this.getCachedData();
    return res[key] as T;
  }

  async clear(key?: string | string[]) {
    if (key) {
      const cacheKeys = Array.isArray(key) ? key : [key];
      // eslint-disable-next-line no-restricted-syntax
      for (const cacheKey of cacheKeys) {
        // eslint-disable-next-line no-await-in-loop
        await this.set(cacheKey, undefined as unknown as T);
      }
    } else {
      await saveMapMesg({
        [this.CACHE_KEY]: '',
      });
    }
  }
}

/**
 * 数据缓存进localStorage
 */
export class LocalStorageCache<T> implements Storage<T> {
  CACHE_KEY: string;

  constructor(cacheKey: string) {
    this.CACHE_KEY = cacheKey;
  }

  async set(key: string, data: T): Promise<this> {
    return this.setSync(key, data);
  }

  async get(key: string): Promise<T | undefined> {
    return this.getSync(key);
  }

  async clear(key?: string | string[] | undefined): Promise<void> {
    this.clearSync(key);
  }

  getCachedDataSync(): Record<string, unknown> {
    const res: any = localStorage.getItem(this.CACHE_KEY);
    return res ? JSON.parse(res) : {};
  }

  setSync(key: CachedKey, data: T) {
    const cacheDataObj = this.getCachedDataSync();
    cacheDataObj[key] = data;
    localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheDataObj));
    return this;
  }

  getSync(key: CachedKey): T | undefined {
    const res = this.getCachedDataSync();
    return res[key] as T;
  }

  clearSync(key?: string | string[]) {
    if (key) {
      const cacheKeys = Array.isArray(key) ? key : [key];
      // eslint-disable-next-line no-restricted-syntax
      for (const cacheKey of cacheKeys) {
        // eslint-disable-next-line no-await-in-loop
        this.set(cacheKey, undefined as unknown as T);
      }
    } else {
      localStorage.removeItem(this.CACHE_KEY);
    }
  }
}

/**
 * 数据缓存进sessionStorage
 */
export class SessionStorageCache<T> implements Storage<T> {
  CACHE_KEY: string;

  constructor(cacheKey: string) {
    this.CACHE_KEY = cacheKey;
  }

  async set(key: string, data: T): Promise<this> {
    return this.setSync(key, data);
  }

  async get(key: string): Promise<T | undefined> {
    return this.getSync(key);
  }

  async clear(key?: string | string[] | undefined): Promise<void> {
    this.clearSync(key);
  }

  getCachedDataSync(): Record<string, unknown> {
    const res: any = sessionStorage.getItem(this.CACHE_KEY);
    return res ? JSON.parse(res) : {};
  }

  setSync(key: CachedKey, data: T) {
    const cacheDataObj = this.getCachedDataSync();
    cacheDataObj[key] = data;
    sessionStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheDataObj));
    return this;
  }

  getSync(key: CachedKey): T | undefined {
    const res = this.getCachedDataSync();
    return res[key] as T;
  }

  clearSync(key?: string | string[]) {
    if (key) {
      const cacheKeys = Array.isArray(key) ? key : [key];
      // eslint-disable-next-line no-restricted-syntax
      for (const cacheKey of cacheKeys) {
        // eslint-disable-next-line no-await-in-loop
        this.set(cacheKey, undefined as unknown as T);
      }
    } else {
      sessionStorage.removeItem(this.CACHE_KEY);
    }
  }
}
